// D-075 store migrations: typed client for packages/domain/import/src/http.ts (migrations.*). A
// migration is a platform connection plus the import jobs it ran (the same jobs as a spreadsheet
// import, shown in Products → Import too).
import { useQuery } from "@tanstack/react-query";
import { request } from "../../api/client";

const S = (orgId: string, storeId: string) =>
  `/orgs/${encodeURIComponent(orgId)}/stores/${encodeURIComponent(storeId)}`;

/** Platforms with a direct connection; the rest move through a spreadsheet. */
export const MIGRATION_SOURCES = ["shopify", "blanxer", "zalient", "pathao"] as const;
export type MigrationSource = (typeof MIGRATION_SOURCES)[number];
export type MigrateKind = "products" | "customers" | "orders";

/** What each platform can bring across today. */
export const SOURCE_KINDS: Record<MigrationSource, MigrateKind[]> = {
  shopify: ["products", "customers", "orders"],
  blanxer: ["products", "customers"],
  zalient: ["products", "customers"],
  pathao: ["products"],
};

export interface MigrationJob {
  id: string;
  kind: MigrateKind;
  state: string;
  rows: { total: number; ok: number; failed: number; skipped: number };
  chunks: { total: number; done: number };
  images: { ok: number; failed: number };
  finished_at: string | null;
}

export interface Migration {
  id: string;
  source: MigrationSource;
  status: "connected" | "running" | "done" | "failed";
  account: { name: string; url?: string };
  kinds: MigrateKind[];
  jobs: Partial<Record<MigrateKind, MigrationJob>>;
  progress: { kind: MigrateKind; read: number } | null;
  last_error: string | null;
  connected_at: string | null;
  last_run_at: string | null;
}

/** What the connect form sends (only the fields of the chosen platform). */
export interface MigrationConnectBody {
  address?: string;
  client_id?: string;
  client_secret?: string;
  access_token?: string;
  api_key?: string;
  email?: string;
  password?: string;
}

export const migrationsApi = {
  list: (o: string, s: string) => request<{ data: Migration[] }>("GET", `${S(o, s)}/migrations`),
  connect: (o: string, s: string, source: MigrationSource, body: MigrationConnectBody) =>
    request<Migration>("POST", `${S(o, s)}/migrations/${source}`, { body }),
  run: (o: string, s: string, source: MigrationSource, kinds: MigrateKind[]) =>
    request<Migration>("POST", `${S(o, s)}/migrations/${source}/run`, { body: { kinds } }),
  disconnect: (o: string, s: string, source: MigrationSource) =>
    request<void>("DELETE", `${S(o, s)}/migrations/${source}`),
};

export const migrationKeys = {
  list: (o: string, s: string) => ["migrations", o, s] as const,
};

/** The store's platform connections; refreshes every 2.5 s while one is importing. */
export function useMigrations(orgId: string, storeId: string) {
  return useQuery({
    queryKey: migrationKeys.list(orgId, storeId),
    queryFn: () => migrationsApi.list(orgId, storeId),
    refetchInterval: (q) =>
      q.state.data?.data.some(
        (m) =>
          m.status === "running" ||
          Object.values(m.jobs).some((j) => j && (j.state === "running" || j.state === "dry_run")),
      )
        ? 2500
        : false,
  });
}
